// Lucrari: stages and calendars, mirroring the rules in the API's WorksService.

import { vatRateAddOnDate } from "@/utils";

// Stage labels follow the status names of the flow (the blue triangles).
export const STAGES = [
  { value: "contractat", text: "Contractat", color: "blue-grey" },
  // advance cashed: the client's finishings are confirmed by hand before the final measurements
  { value: "asteapta_confirmare_finisaje", text: "Asteapta confirmare finisaje", color: "brown" },
  { value: "asteapta_masuratori", text: "Asteapta masuratori", color: "orange" },
  { value: "gata_productie", text: "Gata de productie", color: "blue" },
  { value: "programat_productie", text: "Programat in productie", color: "indigo" },
  { value: "pregatit_montaj", text: "Pregatit de montaj", color: "deep-purple" },
  { value: "programat_montaj", text: "Programat in montaj", color: "teal" },
  { value: "finalizata", text: "Montaj finalizat", color: "green" },
  // the client paid the final invoice after the montage
  { value: "platita", text: "Finalizat platit", color: "green darken-3" },
];

// Interventie is not a stage but a second status of a finished work: it has open interventions
// (problems the client reported). The work stays in Montaj finalizat / Finalizat platit, so the
// stage filters keep finding it.
export const INTERVENTIE = { text: "Interventie", color: "red" };

// Open interventions: counted from the work's list when it is loaded (the work's page, kept
// current as they are added and resolved), else the count the works list comes with.
export function openInterventions(work) {
  if (Array.isArray(work.observations)) {
    return work.observations.filter((o) => o.kind === "interventie" && o.resolved != 1).length;
  }
  return Number(work.open_interventii) || 0;
}

// A finished work with open interventions: shown with the Interventie status, back in the
// active list and bookable for montage (the team's visit to fix it).
export function inInterventie(work) {
  return isClosed(work.stage) && openInterventions(work) > 0;
}

export function stageInfo(stage) {
  return STAGES.find((s) => s.value === stage) || { value: stage, text: stage, color: "grey" };
}

export function stageIndex(stage) {
  return STAGES.findIndex((s) => s.value === stage);
}

// Done: no more bookings, no deadline to watch, out of the default list.
export const CLOSED_STAGES = ["finalizata", "platita"];

export function isClosed(stage) {
  return CLOSED_STAGES.includes(stage);
}

// Who counts as an agent in Lucrari: a fence agent (Users.agent_market 'ro' / 'export'), whatever
// their role - one export agent has the 'user' role - plus anyone with the 'agent' role. Admins and
// superusers are never restricted, even when they are fence agents too. An agent sees only their
// works (isAgentsWork; their bookings in all three calendars); on them they confirm only the advance,
// the final measurements and the finishings, and book measurements - until the work reaches
// production (agentCanChange). Production and montage are read-only for them.
export function isWorksAgent(user) {
  if (!user || ["admin", "superuser"].includes(user.rol)) return false;
  return user.rol === "agent" || !!user.agent_market;
}

// An agent's works: their own - except export agents (Users.agent_market 'export'), who work all
// export works, whoever made the contract. Treated as their own everywhere: listed, opened, acted on.
export function isExportAgent(user) {
  return isWorksAgent(user) && user.agent_market === "export";
}

export function isAgentsWork(user, work) {
  if (isExportAgent(user)) return !!work.country && work.country !== "ro";
  return work.user_id == user.id_user;
}

// The list / events API filters for an agent's works (see isAgentsWork).
export function agentFilters(user) {
  return isExportAgent(user) ? { country: "eu" } : { userId: user.id_user };
}

// Before production (stage under "Gata de productie"): what an agent may still change on a work.
export function agentCanChange(work) {
  return stageIndex(work.stage) < stageIndex("gata_productie");
}

// Are the client's finishings done (masonry, concrete, posts the aluminium is fixed on)? Until
// they are, the final measurements can't be taken. Confirmed by hand ("Confirma finisaje", the
// stage Asteapta confirmare finisaje), so true exactly from Asteapta masuratori on.
export function finisajeDone(work) {
  return work.finisaje_finalizate == 1;
}

// What the agent declared on the contract: true / false, or null when not answered.
export function agentFinisaje(work) {
  const value = (work.contract_detalii || {}).finisaj_finalizat;
  if (value === undefined || value === null || value === "") return null;
  return value === true || value === "true" || value == 1;
}

export function agentFinisajeText(work) {
  const value = agentFinisaje(work);
  return value === null ? "-" : value ? "Finalizate" : "Nefinalizate";
}

// Secondary status: are the slats and colour final, so production may plan the materials?
// Changeable on the work until the final measurements are taken.
export function detaliiDone(work) {
  return work.detalii_stabilite == 1;
}

export function detaliiInfo(work) {
  return detaliiDone(work)
    ? { text: "Detalii stabilite", color: "green", icon: "mdi-clipboard-check" }
    : { text: "Detalii nestabilite", color: "orange", icon: "mdi-clipboard-alert-outline" };
}

export function canChangeDetalii(work) {
  return stageIndex(work.stage) < stageIndex("gata_productie");
}

// Where the montage is: the contract's other montage address when one was ticked ("Alta adresa
// pentru montaj"), else the address on the contract (the client's, as filled in on the contract),
// else the client's city and county. Empty when none is known.
export function montageLocation(work) {
  const d = work.contract_detalii || {};
  if (d.alta_locatie && d.locatie) return d.locatie;
  return d.adresa || [work.client_oras, work.client_county].filter(Boolean).join(", ");
}

// Second flag: the work has to be sent out to be painted. Set by hand by production and admins.
// Anything to paint: the whole work sent out, or single elements listed (e.g. the posts).
export function hasVopsire(work) {
  return needsVopsire(work) || !!(work.vopsire_elemente && String(work.vopsire_elemente).trim());
}

export function needsVopsire(work) {
  return work.vopsire_externa == 1;
}

// The parts sent out to be painted, each with its dates: the whole work and/or the extra elements.
export function vopsireParts(work) {
  const parts = [];
  if (needsVopsire(work)) {
    parts.push({ key: "lucrare", text: "Toata lucrarea", sent: work.data_vopsire_trimisa, back: work.data_vopsire_returnata });
  }
  if (work.vopsire_elemente && String(work.vopsire_elemente).trim()) {
    parts.push({ key: "elemente", text: "Extra elemente", sent: work.data_elemente_trimise, back: work.data_elemente_returnate });
  }
  return parts;
}

// Where the painting is: null (nothing to paint), "de_trimis" (something not sent yet),
// "la_vopsit" (all sent, something still at the painter) or "returnata" (everything is back).
export function vopsireState(work) {
  const parts = vopsireParts(work);
  if (!parts.length) return null;
  if (parts.some((p) => !p.sent)) return "de_trimis";
  if (parts.some((p) => !p.back)) return "la_vopsit";
  return "returnata";
}

// Where one part is (key "lucrare" or "elemente", as in vopsireParts): null when the work does not
// have it, else "de_trimis", "la_vopsit" or "returnata".
export function vopsirePartState(work, key) {
  const part = vopsireParts(work).find((p) => p.key === key);
  if (!part) return null;
  return part.back ? "returnata" : part.sent ? "la_vopsit" : "de_trimis";
}

const VOPSIRE_STATE_TEXT = { de_trimis: "De trimis la vopsit", la_vopsit: "La vopsit", returnata: "Returnata de la vopsit" };

export function vopsireStateText(work) {
  return VOPSIRE_STATE_TEXT[vopsireState(work)] || "Fara vopsire";
}

// Postponed by hand (production, superusers, admins): the work keeps its stage, but is out of the
// default list and the "De programat" lists and takes no bookings until it is resumed.
export const AMANATA = { text: "Amanata", color: "brown" };

export function isAmanata(work) {
  return !!(work && work.data_amanare);
}

export function canPostpone(user) {
  return canChangeVopsire(user);
}

export function canChangeVopsire(user) {
  return !isWorksAgent(user) && ["admin", "superuser", "user"].includes(user && user.rol);
}

// The colours kept in stock. Anything else - and any 200 mm slat, whatever its colour - has to be
// ordered specially, so the works list flags it.
export const STOCK_COLORS = ["7016", "8017", "8019"];

export function hasSpecialColor(work) {
  if (!work.color) return false;
  const code = (String(work.color).match(/\d{4}/) || [])[0];
  const has200 = (work.slat_patterns || []).some((p) => p.widths.some((w) => Number(w) === 200));
  return has200 || !STOCK_COLORS.includes(code);
}

// Slat widths of a work, from the API's slat_patterns: [{ widths: [60, 100, 20], models: [...] }]
// -> "60/100/20, 87". The models go in the tooltip.
export function slatPatternsLabel(patterns) {
  return (patterns || []).map((p) => p.widths.join("/")).join(", ");
}

// What the works list shows for the fence: the models (H60, H125, V20...), or the slat widths of
// modules from older offers, and the total m². Worked out by the API on each list row, which
// leaves the modules themselves out.
export function fenceModels(work) {
  return work.fence_models || [];
}

export function fenceArea(work) {
  return Number(work.fence_area) || 0;
}

export function slatPatternsTitle(patterns) {
  return (patterns || []).map((p) => `${p.widths.join("/")} mm: ${p.models.join(", ") || "-"}`).join("\n");
}

// The action that moves a work out of its current stage, if it is done by hand.
// Stages not listed here move by booking a calendar event.
export const NEXT_ACTION = {
  contractat: { action: "avans_incasat", text: "Avans incasat", icon: "mdi-cash-check" },
  // the client's finishings are done, whatever the agent declared on the contract
  asteapta_confirmare_finisaje: { action: "finisaje_confirmate", text: "Confirma finisaje", icon: "mdi-wall" },
  asteapta_masuratori: { action: "masuratori_luate", text: "Masuratori luate", icon: "mdi-tape-measure" },
  programat_productie: { action: "productie_finalizata", text: "Productie finalizata", icon: "mdi-factory" },
  programat_montaj: { action: "montaj_finalizat", text: "Montaj finalizat", icon: "mdi-check-decagram" },
  // one step, admins only
  finalizata: { action: "marcheaza_platita", text: "Marcheaza platit", icon: "mdi-cash-check", adminOnly: true },
};

export function nextAction(work) {
  return NEXT_ACTION[work.stage] || null;
}

// The three calendars.
//  - minStage: earliest stage a booking is accepted from (enforced by the API)
//  - toSchedule: which works show up in the calendar's "De programat" list
//  - mode: how a booking is made
//      day   = one day, no time (measurements: the scheduler decides how many fit in a day)
//      hours = a start plus working hours, the end follows the working day (production)
//      range = whole days, from - to (montage)
//  - people: who can be assigned - "agents" (HUB users) or an OameniProductie tip
//  - exclusive: a person can't be on two overlapping bookings (production). A montage team
//    does several montages a day, so their other bookings are only shown.
export const CALENDARS = {
  masuratori: {
    type: "masuratori",
    title: "Calendar masuratori",
    icon: "mdi-tape-measure",
    color: "#fb8c00",
    // the visit can be planned as soon as the contract is signed: before the advance is cashed
    // and the finishings are confirmed
    minStage: "contractat",
    toSchedule: (w) => ["contractat", "asteapta_confirmare_finisaje", "asteapta_masuratori"].includes(w.stage) && !w.masuratori_start,
    mode: "day",
    people: "agents",
    peopleLabel: "Agent",
  },
  productie: {
    type: "productie",
    title: "Calendar productie",
    icon: "mdi-factory",
    color: "#3949ab",
    minStage: "gata_productie",
    toSchedule: (w) => w.stage === "gata_productie",
    mode: "hours",
    people: "productie",
    peopleLabel: "Echipa productie",
    exclusive: true,
  },
  montaj: {
    type: "montaj",
    title: "Calendar montaj",
    icon: "mdi-truck-delivery",
    color: "#00897b",
    // montage may be planned as soon as production can be, before production is booked
    minStage: "gata_productie",
    // plus finished works with open interventions and no montage booked from today on: the
    // visit to fix it
    toSchedule: (w) =>
      (["gata_productie", "programat_productie", "pregatit_montaj"].includes(w.stage) && !w.montaj_start) ||
      (inInterventie(w) && !(w.montaj_end && new Date(w.montaj_end.replace(" ", "T")) >= new Date())),
    mode: "range",
    people: "montaj",
    peopleLabel: "Echipa montaj",
  },
};

export function canBook(work, type) {
  const cal = CALENDARS[type];
  const i = stageIndex(work.stage);
  if (isAmanata(work)) return false;
  // a finished work with open interventions takes montage bookings only: the team's visit to fix it
  if (isClosed(work.stage)) return type === "montaj" && inInterventie(work);
  return i >= stageIndex(cal.minStage);
}

// Days left until the montage deadline (negative when past), or null when there is none.
export function daysToDeadline(work, today = new Date()) {
  if (!work.data_montaj_maxima) return null;
  const deadline = new Date(work.data_montaj_maxima + "T00:00:00");
  const start = new Date(today.getFullYear(), today.getMonth(), today.getDate());
  return Math.round((deadline - start) / 86400000);
}

export function deadlineColor(days) {
  if (days === null) return "grey";
  if (days < 0) return "red";
  if (days <= 7) return "orange";
  return "green";
}

// ---------------------------------------------------------------- working day
// Mirrors the API's WorkSchedule, for previews only: the API works out the stored end.
// settings: { work_day_start: "08:00:00", work_day_end: "16:30:00", work_days: "1,2,...,7" }

function minutesOf(time) {
  const [h, m] = String(time || "00:00").split(":").map(Number);
  return h * 60 + m;
}

export function workSchedule(settings) {
  const s = settings || {};
  const dayStart = minutesOf(s.work_day_start || "08:00");
  const dayEnd = minutesOf(s.work_day_end || "16:30");
  const days = String(s.work_days || "1,2,3,4,5,6,7").split(",").map(Number);
  const isoDay = (d) => d.getDay() || 7;
  const minuteOfDay = (d) => d.getHours() * 60 + d.getMinutes();
  const at = (d, minutes) => {
    const r = new Date(d);
    r.setHours(Math.floor(minutes / 60), minutes % 60, 0, 0);
    return r;
  };
  const isWorkingDay = (d) => days.includes(isoDay(d));

  const isWorkingTime = (d) => isWorkingDay(d) && minuteOfDay(d) >= dayStart && minuteOfDay(d) < dayEnd;

  const nextWorkingMoment = (d) => {
    if (isWorkingDay(d) && minuteOfDay(d) < dayStart) return at(d, dayStart);
    if (isWorkingTime(d)) return new Date(d);
    let day = new Date(d);
    // guard: at most a week ahead (there is always a working day in it)
    for (let i = 0; i < 8; i++) {
      day.setDate(day.getDate() + 1);
      if (isWorkingDay(day)) break;
    }
    return at(day, dayStart);
  };

  const addHours = (start, hours) => {
    let cursor = nextWorkingMoment(start);
    let remaining = Math.round(hours * 60);
    for (let guard = 0; guard < 10000; guard++) {
      const available = dayEnd - minuteOfDay(cursor);
      if (remaining <= available) return new Date(cursor.getTime() + remaining * 60000);
      remaining -= available;
      cursor = nextWorkingMoment(at(cursor, dayEnd));
    }
    return cursor;
  };

  return {
    dayStart,
    dayEnd,
    days,
    dayHours: (dayEnd - dayStart) / 60,
    isWorkingTime,
    nextWorkingMoment,
    addHours,
    // "08:00:00" style, for FullCalendar options
    startTime: `${String(Math.floor(dayStart / 60)).padStart(2, "0")}:${String(dayStart % 60).padStart(2, "0")}:00`,
    endTime: `${String(Math.floor(dayEnd / 60)).padStart(2, "0")}:${String(dayEnd % 60).padStart(2, "0")}:00`,
    // FullCalendar weekdays are 0 = Sunday
    hiddenDays: [1, 2, 3, 4, 5, 6, 7].filter((d) => !days.includes(d)).map((d) => d % 7),
  };
}

export function formatHours(hours) {
  const h = Number(hours);
  return (Number.isInteger(h) ? h : h.toFixed(1).replace(".", ",")) + " h";
}

// The "Module gard" value without VAT, as the offer's own screen shows it (valoareGardFaraTva),
// from the figures the API sends with an event (its current offer). An offer priced under the old
// per-model prices carries VAT in val_fara_tvaG - see fenceValueWithVat. Null when there is none.
export function fenceValueNet(e) {
  if (e.val_fara_tvaG == null) return null;
  const fence = Number(e.val_fara_tvaG) || 0;
  return +e.prices_net ? fence : fence / vatRateAddOnDate(e.valoare_data);
}

// Fence prices are in EUR whatever the offer's moneda says (the form defaults it to "ron"); shown
// in RON at the rate passed in, the current one, as the offer's screen converts them.
export function formatRon(valueEur, curs) {
  return Math.round(valueEur * curs).toLocaleString("ro-RO") + " RON";
}
